import { Card, Text, Group, Badge, Stack, ThemeIcon } from '@mantine/core';
import { IconMail, IconAlertTriangle } from '@tabler/icons-react';

interface TopOffendingEmail {
  email: string;
  count: number;
}

interface TopOffendingEmailsProps {
  data: TopOffendingEmail[];
  maxItems?: number;
}

/**
 * Component to display top offending email addresses with attempt counts
 * @param data - Array of email addresses with their attempt counts
 * @param maxItems - Maximum number of items to display (default: 5)
 */
export function TopOffendingEmails({ data, maxItems = 5 }: TopOffendingEmailsProps): JSX.Element {
  const displayData = data.slice(0, maxItems);

  if (displayData.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500} c="dimmed">
            Top Offending Emails
          </Text>
          <ThemeIcon color="green" variant="light" size="sm">
            <IconMail size={16} />
          </ThemeIcon>
        </Group>
        <Text size="sm" c="dimmed">
          No offending emails found
        </Text>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={500} c="dimmed">
          Top Offending Emails
        </Text>
        <ThemeIcon color="orange" variant="light" size="sm">
          <IconAlertTriangle size={16} />
        </ThemeIcon>
      </Group>
      
      <Stack gap="xs">
        {displayData.map((item, index) => (
          <Group key={item.email} justify="space-between" p="xs" style={{ 
            backgroundColor: 'var(--mantine-color-gray-0)', 
            borderRadius: 'var(--mantine-radius-sm)' 
          }}>
            <Group gap="xs">
              <Badge size="sm" color="orange" variant="light">
                #{index + 1}
              </Badge>
              <Text size="sm" fw={500} style={{ wordBreak: 'break-all' }}>
                {item.email}
              </Text>
            </Group>
            <Badge color="orange" variant="filled" size="sm">
              {item.count} attempts
            </Badge>
          </Group>
        ))}
      </Stack>
      
      {data.length > maxItems && (
        <Text size="xs" c="dimmed" mt="sm" ta="center">
          +{data.length - maxItems} more emails
        </Text>
      )}
    </Card>
  );
}
